import { MasterKey } from '@overtur-monorepo/keying-hierarchy'
import { KeyNodeType, KeywayType } from '@overtur-monorepo/keying-shared'
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core'
import { MatDialog, MatDialogState } from '@angular/material/dialog'
import { Subject } from 'rxjs'
import { take, takeUntil } from 'rxjs/operators'
import { fadeIn } from '../../animations/fadeIn'
import { slideInOnlyUp } from '../../animations/slideInOnlyUp'
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component'
import { showConfirm } from '../../functions/dialog.helper'
import { getKeySectionConflictMessage, getKeySectionConflictTitle } from '../../functions/message.helper'
import { Book, Cylinder, KeySystem, Library, Structure } from '../../models/dev.test.models'
import { KeyingDialogType } from '../../models/keying-dialog-type.enum'
import { DialogService } from '../../services/dialog.service'
import { ErrorService } from '../../services/error.service'
import { FormatDataService } from '../../services/format.data.service'
import { KeyingBookService } from '../../services/keying.book.service'
import { KeyingCylinderService } from '../../services/keying.cylinder.service'
import { KeyingDataSetsService } from '../../services/keying.data.sets.service'
import { KeyingLibraryService } from '../../services/keying.library.service'
import { MasterKeyService } from '../../services/keying.masterkey.service'
import { LoadingService } from '../../services/loading.service'
import { KeyBittingMatrixComponent } from './key-bitting-matrix/key-bitting-matrix.component'
import { SelectedMasterKeyService } from '@overtur-monorepo/keying-hierarchy'
import { KeyingSystemService } from '../../services/keying.system.service'
import { MasterKeyService as LibMasterKeyService } from '@overtur-monorepo/keying-hierarchy'
import { ChangeLogConfirmationDialogComponent } from './change-log-confirmation-dialog/change-log-confirmation-dialog.component'
import { LoggingService } from '../../services/logging.service'
import { KeyingAuthService } from '../../services/keying.auth.service'
import { User } from '@overtur-monorepo/keying-user'

@Component({
    selector: 'app-matrix',
    templateUrl: './matrix.component.html',
    styleUrls: ['./matrix.component.scss'],
    animations: [fadeIn, slideInOnlyUp],
    standalone: false
})
export class MatrixComponent implements OnInit, OnDestroy {
  @ViewChild(KeyBittingMatrixComponent) keyBittingMatrixComponent: KeyBittingMatrixComponent
  destroy$ = new Subject()
  keyNodeType = KeyNodeType

  @Input() structure: Structure
  @Input() mkSideBar
  @Input() selectedMasterKey: MasterKey
  @Input() selectedTab
  @Input() keySystem: KeySystem
  @Input() selectedBook: Book
  @Input() assignedMasterKeyBittings = []
  @Input() selectedCylinder: Cylinder
  @Input() library: Library
  @Input() enablePrepareForMECS = false

  @Output() requestChanged = new EventEmitter<any>()
  @Output() finished = new EventEmitter<any>()
  @Output() validate = new EventEmitter<any>()
  @Output() bookWritten = new EventEmitter<any>()

  @Output() addKey = new EventEmitter()
  @Output() removeKey = new EventEmitter()

  user: User
  request
  matrix
  bittingDataSets
  isMatchingPublishedMasterKeyFound
  writeBookBtn = false
  updatedKwyId
  isKeyWayUpdated = false
  loading

  constructor(
    private keyingLibraryService: KeyingLibraryService,
    public keyingDataSetsService: KeyingDataSetsService,
    private keyingAuthService: KeyingAuthService,
    public keyingBookService: KeyingBookService,
    private formatDataService: FormatDataService,
    private loadingService: LoadingService,
    private errorService: ErrorService,
    private keyingCylinderService: KeyingCylinderService,
    private dialog: MatDialog,
    private dialogService: DialogService,
    private masterKeyService: MasterKeyService,
    private selectedMasterKeyService: SelectedMasterKeyService,
    private keyingSystemService: KeyingSystemService,
    private libMasterKeyService: LibMasterKeyService,
    private loggingService: LoggingService
  ) {}

  /*istabul ignore next*/
  ngOnInit(): void {
    this.keyingBookService.bittingDataSets.pipe(takeUntil(this.destroy$)).subscribe((bittingData) => {
      if (!bittingData) {
        return
      }

      this.bittingDataSets = bittingData
      if (!this.selectedBook) {
        this.matrix = this.bittingDataSets.matrix
      } else if (this.selectedBook) {
        this.matrix = this.selectedBook
      }
    })

    this.keyingCylinderService.onDeleteCylinderSelected$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (value) {
        this.deleteCylinder()
      }
    })

    this.subscribeToUser();

    this.loadingService.loading$.pipe(takeUntil(this.destroy$)).subscribe((loading) => {
      this.loading = loading
    })
  }

  ngOnDestroy() {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  subscribeToUser() {
    // Subscribe to User
    this.keyingAuthService.keyingUser$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      if (!user) {
        return
      }
      this.user = user
    })
  }

  updateRequest(request) {
    this.request = request
    this.requestChanged.emit(this.request)
  }

  disableWriteBookBtn(request) {
    this.writeBookBtn = request
  }

  updateKeyWayId(keyWayId) {
    this.isKeyWayUpdated = true
    this.updatedKwyId = keyWayId
  }

  saveMatrix() {
    if (this.selectedTab.id === 0) {
      this.loadingService.set({ isLoading: true, message: 'Updating Attributes' })
      this.keyingBookService
        .putMatrix(this.keySystem.id, this.selectedMasterKey.id, this.request)
        .pipe(take(1))
        .subscribe(
          () => {
            this.loadingService.set({ isLoading: false, message: '' })
            this.keyBittingMatrixComponent.loadKeySections()
            this.errorService.confirm('KBA/SOP Saved. Key Section cannot be updated here and has been reset.')
          },
          (error) => {
            this.errorService.notify('No Changes detected', 'There were no changes made to KBA or SOP.')
          }
        )
    } else if (this.selectedTab.id === 1) {
      if (this.selectedCylinder.isPublished || this.selectedCylinder.isLegacyPublished) {
        const data = {
          cancel: 'Cancel',
          confirm: 'Confirm',
          actionType: 'Cylinder Updated',
          showCancel: true,
          reasonRequired: false,
          keySystemId: this.keySystem.id,
          source: this.selectedCylinder.name,
        }
        data.reasonRequired = true
        const dialogRef = this.dialogService.open(ChangeLogConfirmationDialogComponent, data, false)

        dialogRef.afterClosed().subscribe((result) => {
          if (result.confirmed) {
            this.updateCylinder(result.auditTrailRequest)
          }
        })
      } else {
        this.updateCylinder()
      }
    } else if (this.selectedTab.id === 2) {
      this.loadingService.set({ isLoading: true, message: 'Saving RCK' })
      const body = [
        {
          op: 'replace',
          path: '/LostBallDeActivateKeyType',
          value: this.selectedMasterKey?.rck?.attribute?.lostBallDeActivateKeyType,
        },
        {
          op: 'replace',
          path: '/LostBallChamber',
          value: this.selectedMasterKey?.rck?.attribute?.lostBallChamber,
        },
      ]
      this.loggingService.log('body', body)

      this.masterKeyService
        .patchConstructionKey(body, this.structure.id, this.keySystem.id, this.selectedMasterKey.id)
        .pipe(take(1))
        .subscribe(
          () => {
            this.loadingService.set({ isLoading: false, message: '' })
          },
          (error) => {
            this.loadingService.set({ isLoading: false, message: '' })
            this.errorService.throw(error)
          }
        )
    }
  }

  updateCylinder(auditTrailRequest: any = undefined) {
    const body = {
      keywayId: this.isKeyWayUpdated ? this.updatedKwyId : this.selectedCylinder.keywayId,
      AuditTrailRequest: auditTrailRequest,
    }

    this.loggingService.log('body', body)
    this.loadingService.set({ isLoading: true, message: 'Updating Cylinder' })
    this.keyingCylinderService
      .patchCylinder(
        this.structure.id,
        this.keySystem.id,
        this.selectedCylinder.masterKeyId,
        this.selectedCylinder.id,
        body
      )
      .pipe(take(1))
      .subscribe(
        () => {
          this.validateCylinder()
          this.loadingService.set({ isLoading: false, message: '' })
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        }
      )
  }

  validateCylinder() {
    this.loadingService.set({ isLoading: true, message: 'Validating Cylinder' })
    this.keyingCylinderService
      .validateCylinder(
        this.structure.id,
        this.keySystem.id,
        this.selectedCylinder.masterKeyId,
        this.selectedCylinder.id
      )
      .subscribe(
        () => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.keyingDataSetsService.loadStructureDetails(this.structure.id)
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        }
      )
  }

  validateAndGenerateBook() {
    if (this.request.keywayTypeId === KeywayType.Elevated) {
      this.openElevatedKeyTypeConfirmation()
    } else {
      this.generateBook()
    }
  }

  private generateBook() {
    this.loadingService.set({ isLoading: true, message: 'Writing Book' })
    this.keyingBookService
      .SaveBook(this.keySystem.id, this.selectedMasterKey.id, this.request)
      .pipe(take(1))
      .subscribe(
        () => {
          this.keyingBookService.loadFormattedCombinations(
            this.structure,
            this.keySystem.id,
            this.selectedMasterKey,
            null,
            null,
            null,
            true
          )
          this.loadingService.set({ isLoading: false, message: '' })
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        }
      )
  }

  private openElevatedKeyTypeConfirmation() {
    const data = {
      title: 'Elevated Key Confirmation',
      message: 'Book issued in elevated key section. Please confirm.',
      cancel: 'Cancel',
      confirm: 'Ok',
      showCancel: true,
    }

    const dialogRef = this.dialogService.open(ConfirmDialogComponent, data, false)
    dialogRef.afterClosed().subscribe((result) => {
      if (result.confirmed) {
        this.generateBook()
      }
    })
  }

  cancel() {
    this.finished.emit()
  }

  saveBittings(auditTrailRequest: any = undefined): void {
    const combinationsAssginedBittings = {
      //AssignedMasterKeyBittings: this.assignedMasterKeyBittings,      
      AuditTrailRequest: auditTrailRequest || {},
      KeyBittingUpdateRequests: this.assignedMasterKeyBittings,
    }
    this.loadingService.set({ isLoading: true, message: 'Updating Book' })
    this.keyingBookService
      .updateBook(this.keySystem.id, this.selectedMasterKey.id, this.selectedBook.id, combinationsAssginedBittings)
      .pipe(take(1))
      .subscribe(
        (combination) => {
          if (!combination) {
            return
          }
          this.finished.emit()
          /* Get Structure Details Everytime A Book Is Saved */
          this.loadingService.set({ isLoading: false, message: '' })
          this.keyingDataSetsService.loadStructureDetails(this.structure.id)
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        }
      )
  }

  private showDialog(keys: string[]): void {
    const message = getKeySectionConflictMessage(keys)
    const title = getKeySectionConflictTitle()
    showConfirm(this.dialog, KeyingDialogType.CONFIRM, message, title)
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.saveBittings()
        }
      })
  }

  private getKeyNames(keys: MasterKey[]): string[] {
    const keyNames = []
    keys.forEach((key) => {
      if (key.keywayId !== this.selectedBook.keywayId) {
        keyNames.push(key.keySetSymbol)
      }
    })
    return keyNames
  }

  private getMasterKeysWithKeySection(): MasterKey[] {
    const masterKeyIds = this.assignedMasterKeyBittings.reduce(
      (acc, next) => (next.masterKeyId && acc.push(next.masterKeyId), acc),
      []
    )
    const masterKeys = this.selectedMasterKey.children?.filter((c) => masterKeyIds.includes(c.id))
    return masterKeys.filter((k) => k.keywayId)
  }

  checkIfKeysArePublished() {
    const assignedMasterKeyBittings = this.assignedMasterKeyBittings.filter((m) => !isNaN(m.masterKeyId))

    let unmatchedKeySetSymbols: string[] = []

    assignedMasterKeyBittings.forEach((assignedMasterKeyedBitting) => {
      const masterKeyChild = this.selectedMasterKey.children.find(
        (m) => m.id === assignedMasterKeyedBitting.masterKeyId
      )
      if (
        masterKeyChild &&
        (masterKeyChild.isPublished || masterKeyChild.isLegacyPublished) &&
        masterKeyChild.masterKeyTypeId !== this.keyNodeType.ChangeKeyThruNode &&
        masterKeyChild.masterKeyTypeId !== this.keyNodeType.SingleKeyDifferentThruNode &&
        masterKeyChild.bitting !== assignedMasterKeyedBitting.bitting
      ) {
        unmatchedKeySetSymbols.push(masterKeyChild.keySetSymbol)
      }
    })

    const checkIfUnassignKeyIsPulished = this.unAssginIsPublished()
    const checkIfReassignedIsPublished = this.reAssginIsPublished()

    if (
      checkIfUnassignKeyIsPulished.length > 0 ||
      checkIfReassignedIsPublished.length > 0 ||
      unmatchedKeySetSymbols.length > 0
    ) {
      let keysetSymbols: string = ''

      if (checkIfUnassignKeyIsPulished.length) {
        keysetSymbols = checkIfUnassignKeyIsPulished.map((m) => m.keySetSymbol).join(', ')
      }

      if (checkIfReassignedIsPublished.length) {
        if (keysetSymbols.length) {
          keysetSymbols += ', '
        }

        keysetSymbols += checkIfReassignedIsPublished.map((m) => m.keySetSymbol).join(', ')
      }

      if (unmatchedKeySetSymbols) {
        if (keysetSymbols.length) {
          keysetSymbols += ', '
        }

        keysetSymbols += unmatchedKeySetSymbols.join(', ')
      }

      const data = {
        cancel: 'Cancel',
        confirm: 'Confirm',
        actionType: 'Key Updated',
        showCancel: true,
        reasonRequired: false,
        keySystemId: this.keySystem.id,
        source: keysetSymbols,
      }
      data.reasonRequired = true
      const dialogRef = this.dialogService.open(ChangeLogConfirmationDialogComponent, data, false)

      dialogRef.afterClosed().subscribe((result) => {
        if (result.confirmed) {
          this.validateKeySections(result.auditTrailRequest)
        }
      })
    } else {
      this.validateKeySections()
    }
  }

  validateKeySections(auditTrailRequest: any = undefined) {
    const masterKeysWithKeySection = this.getMasterKeysWithKeySection()
    if (masterKeysWithKeySection.length > 0) {
      const keyNames = this.getKeyNames(masterKeysWithKeySection)
      if (keyNames.length > 0) {
        this.showDialog(keyNames)
      } else {
        this.saveBittings(auditTrailRequest)
      }
    } else {
      this.saveBittings(auditTrailRequest)
    }
  }

  unAssginIsPublished() {
    const unAssginedKeys = this.selectedMasterKey.children?.filter(
      (c) =>
        c.isAssigned &&
        (c.isPublished || c.isLegacyPublished) &&
        c.unassignedLocally &&
        c.masterKeyTypeId !== this.keyNodeType.ChangeKeyThruNode &&
        c.masterKeyTypeId !== this.keyNodeType.SingleKeyDifferentThruNode
    )
    return unAssginedKeys
  }

  reAssginIsPublished() {
    const reAssginedKeys = this.selectedMasterKey.children?.filter(
      (c) =>
        c.masterKeyTypeId !== this.keyNodeType.ConstructionKeyMaster &&
        c.masterKeyTypeId !== this.keyNodeType.OrphanMaster &&
        c.masterKeyTypeId !== this.keyNodeType.CrossKeyMaster &&
        c.masterKeyTypeId !== this.keyNodeType.ConstructionKey &&
        !c.isAssigned && !c.isFutureKey &&        
        (c.isPublished || c.isLegacyPublished) &&
        c.masterKeyTypeId !== this.keyNodeType.ChangeKeyThruNode &&
        c.masterKeyTypeId !== this.keyNodeType.SingleKeyDifferentThruNode
    )
    return reAssginedKeys
  }

  saveBook() {
    this.checkIfKeysArePublished()
  }

  deleteBook(auditTrailRequest: any = undefined) {
    this.loadingService.set({ isLoading: true, message: 'Deleting Book' })
    this.keyingBookService
      .deleteBook(this.keySystem.id, this.selectedMasterKey.id, this.selectedBook.id, auditTrailRequest)
      .pipe(take(1))
      .subscribe(
        () => {
          this.library = new Library()
          this.loadingService.set({ isLoading: false, message: '' })
          this.keyingLibraryService.selectBook(null)
          this.keyingBookService.loadFormattedCombinations(this.structure, this.keySystem.id, this.selectedMasterKey)
          this.keyingDataSetsService.loadKeySystemDataSets(this.structure.id, this.keySystem.id)
          /* Get Structure Details Everytime Deleted A Book */
          this.keyingDataSetsService.loadStructureDetails(this.structure.id)
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        }
      )
  }

  saveCylinder() {
    const auditTrailRequest = {
      reasonCode: null,
      description: null,
    }
    this.loadingService.set({ isLoading: true, message: 'Saving Cylinder' })
    this.keyingCylinderService
      .putCylinderShearLines(
        this.structure.id,
        this.selectedCylinder.keySystemId,
        this.selectedCylinder.masterKeyId,
        this.selectedCylinder.id,
        true,
        this.selectedCylinder.shearLines,
        auditTrailRequest
      )
      .pipe(take(1))
      .subscribe(
        () => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.keyingCylinderService.loadCylinders(this.structure, this.keySystem.id, this.selectedMasterKey.id)
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        }
      )
  }

  prepareForMecs() {
    // this.loadingService.set({isLoading: true, message: 'Preparing For MECS'})
    this.validate.emit()
  }

  autoSet(id, min) {
    const width = document.getElementById(id).style.width

    if (this[id].show && width && Number(width.replace('px', '')) < Number(min.replace('px', ''))) {
      this[id].show = false
      document.getElementById(id).style.width = '11px'
    } else if (!this[id].show) {
      this[id].show = true
      document.getElementById(id).style.width = this[id].width
    }
  }

  checkKeyDataSets() {
    this.keyingDataSetsService.keySystemDataSets$.pipe(takeUntil(this.destroy$)).subscribe((res) => {
      const masterKeyIds = this.selectedBook.allBittings.map((m) => m.assignedMasterKeyId)

      this.isMatchingPublishedMasterKeyFound = false

      for (let i = 0; i < res.masterKeysKeying.children.length; i++) {
        const child = res.masterKeysKeying.children[i]

        // Check if there's a match at Children root level
        if (masterKeyIds.some((m) => m === child.id && child.isPublished)) {
          this.isMatchingPublishedMasterKeyFound = true
        }

        if (!this.isMatchingPublishedMasterKeyFound) {
          this.isMatchingPublishedMasterKeyFound = child.children.some((subChild) =>
            masterKeyIds.some((m) => m === subChild.id && subChild.isPublished)
          )
        }

        if (this.isMatchingPublishedMasterKeyFound) {
          break
        }
      }
    })
  }

  confirmDelete() {
    if (this.selectedTab.id === 0) {
      if (this.selectedBook?.assignmentString) {
        const data = {
          title: 'Delete Book',
          message: 'Books cannot be deleted until all the keys are unassigned first.',
          cancel: 'Cancel',
          confirm: 'Ok',
          showCancel: true,
        }

        this.dialogService.open(ConfirmDialogComponent, data, false)
      } else {
        this.checkKeyDataSets()
        if (this.isMatchingPublishedMasterKeyFound) {
          const data = {
            cancel: 'Cancel',
            confirm: 'Confirm',
            actionType: 'Book Deleted',
            showCancel: true,
            reasonRequired: false,
            keySystemId: this.keySystem.id,
            source: this.selectedBook.assignmentString,
          }
          data.reasonRequired = true
          const dialogRef = this.dialogService.open(ChangeLogConfirmationDialogComponent, data, false)

          dialogRef.afterClosed().subscribe((result) => {
            if (result.confirmed) {
              this.deleteBook(result.auditTrailRequest)
            }
          })
        } else {
          this.deleteBook()
        }
      }
    } else if (this.selectedTab.id === 1) {
      this.deleteCylinder()
    }
  }

  getCompetitorName() {
    let cycLFICPinName
    if (!this.selectedCylinder.isDefaultCylinder) {
      if (this.selectedCylinder.competitorId === 3) {
        cycLFICPinName = this.selectedCylinder.name + '(SAR)'
        return cycLFICPinName
      } else if (this.selectedCylinder.competitorId === 4) {
        cycLFICPinName = this.selectedCylinder.name + '(COR)'
        return cycLFICPinName
      } else {
        cycLFICPinName = this.selectedCylinder.name
        return cycLFICPinName
      }
    } else {
      return this.selectedCylinder.name
    }
  }

  /*istanbul ignore next*/
  deleteCylinder() {
    const addCylinderCompetitorName = this.getCompetitorName()

    if (this.selectedCylinder.isPublished || this.user.isSuperUser) {
      const data = {
        cancel: 'Cancel',
        confirm: 'Confirm',
        actionType: 'Cylinder Deleted',
        showCancel: true,
        reasonRequired: false,
        keySystemId: this.keySystem.id,
        source: addCylinderCompetitorName,
      }
      data.reasonRequired = true
      const dialogRef = this.dialogService.open(ChangeLogConfirmationDialogComponent, data, false)

      dialogRef.afterClosed().subscribe((result) => {
        if (result.confirmed) {
          this.checkCyclinderHasOpenOrder(result.auditTrailRequest)
        }
      })
    } else {
      this.checkCyclinderHasOpenOrder()
    }
  }

  checkCyclinderHasOpenOrder(auditTrailRequest: any = undefined) {
    const addCylinderCompetitorName = this.getCompetitorName()

    this.loadingService.set({
      isLoading: true,
      message: 'Checking if the cylinder has an order neither published nor rejected',
    })
    this.keyingCylinderService
      .cylinderHasOpenOrder(
        this.structure.id,
        this.keySystem.id,
        this.selectedCylinder.masterKeyId,
        this.selectedCylinder.id
      )
      .pipe(take(1))
      .subscribe((res) => {
        this.loadingService.set({ isLoading: false, message: '' })
        if (res) {
          this.errorService.showError(
            `${this.selectedCylinder.name} cannot be deleted until the order is rejected or published`
          )
        } else {
          let message = ''
          if (!this.selectedCylinder.isPublished) {
            message = `"${addCylinderCompetitorName} will be deleted"`
            const data = {
              title: 'Delete Cylinder',
              message: message,
              cancel: 'Cancel',
              confirm: 'Delete',
              showCancel: true,
            }
            const dialogRef = this.dialogService.open(ConfirmDialogComponent, data, false)
            dialogRef.afterClosed().subscribe((result) => {
              if (result.confirmed) {
                this.deleteCylinderAfterConfirmation()
              }
            })
          } else {
            this.deleteCylinderAfterConfirmation(auditTrailRequest)
          }
        }
      })
  }

  deleteCylinderAfterConfirmation(auditTrailRequest: any = undefined) {
    this.loadingService.set({ isLoading: true, message: 'Deleting Cylinder' })
    this.keyingCylinderService
      .deleteCylinder(
        this.structure.id,
        this.keySystem.id,
        this.selectedCylinder.masterKeyId,
        this.selectedCylinder.id,
        auditTrailRequest
      )
      .pipe(take(1))
      .subscribe(
        () => {
          this.loadingService.set({ isLoading: false, message: '' })

          const crossKeyMaster = this.keySystem.masterKeysKeying.children.find(
            (m) => m.masterKeyTypeId === KeyNodeType.CrossKeyMaster
          )

          let masterKeyToSelect: MasterKey
          let deletedCrossKeyChild: MasterKey

          deletedCrossKeyChild = crossKeyMaster.children.find((m) => m.id === this.selectedCylinder.masterKeyId)

          // Select a child if exist or Root key (TMK)
          masterKeyToSelect = crossKeyMaster.children.find((m) => m.id !== this.selectedCylinder.masterKeyId)
          masterKeyToSelect = masterKeyToSelect || this.keySystem.masterKeysKeying

          this.selectedMasterKeyService.setSelectedMasterKey(masterKeyToSelect)
          this.libMasterKeyService.setSelectedMasterKey(masterKeyToSelect)
          this.keyingCylinderService.loadCylinders(this.structure, this.keySystem.id, masterKeyToSelect.id)
          // this.keyingSystemService.deleteMasterKey(deletedCrossKeyChild)
          this.keyingDataSetsService.loadKeySystemDataSets(this.structure.id, this.keySystem.id)

          this.keyingCylinderService.setSelectedCylinder(null)
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        }
      )
  }
  isDefaultCrossKeyedCylinder(): boolean {
    return this.selectedCylinder && this.selectedCylinder.cylinderTypeId === 4
  }

  hotkey(type) {
    this.dialogService.serviceDialogRef$.pipe(take(1)).subscribe((dialogRef) => {
      if (!dialogRef || dialogRef.getState() === MatDialogState.CLOSED) {
        switch (type) {
          case 's1':
            this.validateAndGenerateBook()
            break
          case 's2':
            this.saveBook()
            break
          case 'c':
            this.cancel()
            break
        }
      }
    })
  }
}
